import * as React from 'react';
import { Text, Box, Group, ActionIcon, Tooltip, createStyles } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { IconCircleCheckFilled, IconEdit, IconAlertCircleFilled } from '@tabler/icons-react';

// Components
import { InterpolatedFormCopy } from '../../InterpolatedFormCopy';
import { FieldLabelTooltip } from '../../FieldLabelTooltip';

// Manifest
import { IQuestionConfig } from '../../../../../manifests/types';

// Hooks
import { useDisplayManifestAnswer } from '../../../../../hooks/useDisplayManifestAnswer';
import { useFieldTranslation } from '../../../hooks/useFieldTranslation';

// Types
import { ISurveyTargetPeriod } from '../../../../../types';

const useStyles = createStyles((theme) => ({
    container: {
        borderTop: `1px solid ${theme.colors.gray[3]}`,
        padding: `${theme.spacing.sm} ${theme.spacing.md}`,
        background: '#fff'
    },
    noTopBorder: {
        borderTop: 'none'
    },
    questionGroup: {
        flexWrap: 'nowrap',
        alignItems: 'center'
    },
    questionTitle: {
        flexWrap: 'nowrap',
        alignItems: 'flex-start'
    },
    highlight: {
        background: theme.colors.orange[0]
    },
    canHighlight: {
        cursor: 'pointer'
    }
}));

interface IAnswerSummaryProps {
    value: any;
    answerConfig: IQuestionConfig;
    ancillaryData?: Record<string, any>;
    onClick?: () => void;
    readOnly?: boolean;
    targetperiod?: ISurveyTargetPeriod;
    noTopBorder?: boolean;
    onHighlightQuestion?: (qid: string) => void;
    highlightedQuestions?: string[];
}

export const AnswerSummary: React.FC<IAnswerSummaryProps> = ({
    value,
    answerConfig,
    ancillaryData,
    onClick,
    targetperiod,
    readOnly,
    noTopBorder,
    onHighlightQuestion,
    highlightedQuestions = []
}) => {
    const { t } = useTranslation();
    const { classes, cx } = useStyles();
    const { formatManifestAnswer } = useDisplayManifestAnswer();
    const isHighlighted = React.useMemo(() => {
        if (answerConfig?.key) {
            return highlightedQuestions?.includes(answerConfig.key);
        } else {
            return false;
        }
    }, [highlightedQuestions, answerConfig]);
    const label = useFieldTranslation({
        key: `${answerConfig?.key}.label`,
        targetPeriod: targetperiod,
        hasMarkup: answerConfig?.componentProps?.labelHasMarkup
    });

    return answerConfig && (!answerConfig?.isModule || answerConfig?.isValueTable) ? (
        <Box
            className={cx(classes.container, {
                [classes.noTopBorder]: noTopBorder,
                [classes.highlight]: isHighlighted,
                [classes.canHighlight]: readOnly && typeof onHighlightQuestion !== 'undefined'
            })}
            onClick={
                readOnly && typeof onHighlightQuestion !== 'undefined' && !answerConfig?.componentProps?.computed
                    ? () => {
                          onHighlightQuestion(answerConfig.key);
                      }
                    : undefined
            }
        >
            <Group position="apart" className={classes.questionGroup}>
                <Box style={{ flex: '1' }}>
                    <Group position="apart" className={classes.questionTitle}>
                        {/* {answerConfig?.componentProps?.labelHasMarkup ? (
                            <span>{label}</span>
                        ) : (
                            <InterpolatedFormCopy
                                copy={t(`${answerConfig?.key}.label`, { ns: 'questions' }) ?? ''}
                                data={{}}
                                targetperiod={targetperiod}
                            />
                        )} */}
                        <Group spacing={0}>
                            <span>{label}</span>
                            {answerConfig?.componentProps?.showTooltipOnReview &&
                            answerConfig?.componentProps?.tooltipKey ? (
                                <FieldLabelTooltip translationKey={answerConfig?.componentProps?.tooltipKey} />
                            ) : null}
                        </Group>
                        {!readOnly || (isHighlighted && typeof onHighlightQuestion === 'undefined') ? (
                            <Group>
                                {isHighlighted && typeof onHighlightQuestion === 'undefined' ? (
                                    <Tooltip
                                        label={t('questionHighlightedForClientsAttention', { ns: 'common' })}
                                        withArrow
                                    >
                                        <ActionIcon color="orange" variant="transparent" onClick={onClick}>
                                            <IconAlertCircleFilled size={18} />
                                        </ActionIcon>
                                    </Tooltip>
                                ) : null}
                                {!readOnly && !answerConfig?.componentProps?.computed && !answerConfig?.noReviewEdit ? (
                                    <ActionIcon color="blue" variant="light" onClick={onClick}>
                                        <IconEdit size={14} />
                                    </ActionIcon>
                                ) : null}
                            </Group>
                        ) : null}
                    </Group>
                    <Text fw={500}>{formatManifestAnswer(value, answerConfig, ancillaryData)}</Text>
                </Box>
                {isHighlighted && typeof onHighlightQuestion !== 'undefined' ? (
                    <ActionIcon color="orange" variant="transparent" onClick={onClick}>
                        <IconCircleCheckFilled size={18} />
                    </ActionIcon>
                ) : null}
            </Group>
        </Box>
    ) : null;
};
